import { useState } from "react";
import { toast } from "sonner";
import { Reveal, SectionHeader } from "./Reveal";
import WhatsAppIcon from "./WhatsAppIcon";
import { CONTACT, waLink, WA_NUMBER } from "../lib/site";

const FIELD =
    "w-full bg-white border border-ink/15 rounded-[3px] px-4 py-3 font-body text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/40 transition-colors";

const fmtDate = (v) => {
    if (!v) return "—";
    const [y, m, d] = v.split("-");
    return d ? `${d}/${m}/${y}` : v;
};

export default function ContactSection() {
    const [form, setForm] = useState({
        nome: "",
        whatsapp: "",
        produto: "",
        quantidade: "",
        data: "",
        ideia: "",
        refFile: "",
        obs: "",
    });

    const set = (key) => (e) =>
        setForm((f) => ({
            ...f,
            [key]:
                key === "refFile" && e.target.files
                    ? e.target.files[0]?.name || ""
                    : e.target.value,
        }));

    const submit = (e) => {
        e.preventDefault();
        const msg = [
            "Olá! Vim pelo site da Magnus Personalizados e gostaria de solicitar um orçamento.",
            "",
            `Nome: ${form.nome}`,
            `WhatsApp: ${form.whatsapp}`,
            `Produto desejado: ${form.produto}`,
            `Quantidade: ${form.quantidade || "—"}`,
            `Preciso para: ${fmtDate(form.data)}`,
            `Ideia: ${form.ideia || "—"}`,
            `Foto/referência: ${form.refFile || "—"}${form.refFile ? " (envio o arquivo no chat)" : ""}`,
            `Observações: ${form.obs || "—"}`,
        ].join("\n");
        toast.success(
            "Orçamento preparado! Abrindo o WhatsApp com sua mensagem...",
        );
        window.open(waLink(msg), "_blank");
    };

    return (
        <section id="contato" className="bg-sand py-20 sm:py-28 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
                <div className="lg:col-span-5">
                    <SectionHeader
                        eyebrow="Contato"
                        title={
                            <>
                                Vamos transformar sua ideia em
                                <br />
                                <span className="italic text-gold-deep">
                                    algo especial?
                                </span>
                            </>
                        }
                        sub="Conte para a gente o que você está imaginando. Envie sua ideia, foto, referência ou simplesmente explique o que você gostaria de criar."
                    />

                    <div className="space-y-5 mt-10">
                        {[
                            {
                                label: "WhatsApp",
                                value: CONTACT.whatsappDisplay,
                                href: waLink(
                                    "Olá! Vim pelo site da Magnus Personalizados e gostaria de solicitar um orçamento.",
                                ),
                            },
                            {
                                label: "Instagram",
                                value: CONTACT.instagram,
                                href: CONTACT.instagramUrl,
                            },
                            {
                                label: "E-mail",
                                value: CONTACT.email,
                                href: `mailto:${CONTACT.email}`,
                            },
                        ].map((item) => (
                            <Reveal key={item.label} delay={0.1}>
                                <div className="flex flex-col">
                                    <span className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-deep">
                                        {item.label}
                                    </span>
                                    <a
                                        data-testid={`contact-${item.label.toLowerCase()}-link`}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-body text-base text-ink hover:text-gold-deep transition-colors mt-1"
                                    >
                                        {item.value}
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.2}>
                        <a
                            data-testid="contact-whatsapp-cta"
                            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
                                "Olá! Vim pelo site da Magnus Personalizados e gostaria de solicitar um orçamento.",
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-10 inline-flex items-center gap-3 bg-[#128C7E] text-white font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] px-8 py-4 rounded-full hover:bg-[#1EBE57] transition-all duration-300 hover:-translate-y-0.5"
                        >
                            <WhatsAppIcon size={18} />
                            Fale com a Magnus no WhatsApp
                        </a>
                    </Reveal>
                </div>

                <div className="lg:col-span-7" id="orcamento">
                    <Reveal delay={0.15}>
                        <form
                            data-testid="quote-form"
                            onSubmit={submit}
                            className="bg-white border border-gold/30 rounded-[4px] p-6 sm:p-10 shadow-frame"
                        >
                            <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-deep">
                                Solicite um orçamento
                            </p>
                            <h3 className="font-display text-2xl sm:text-3xl font-medium text-ink mt-3">
                                Peça seu orçamento sem compromisso
                            </h3>

                            <div className="grid sm:grid-cols-2 gap-5 mt-8">
                                <div>
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Nome *
                                    </label>
                                    <input
                                        data-testid="quote-form-field-nome"
                                        required
                                        value={form.nome}
                                        onChange={set("nome")}
                                        className={FIELD}
                                        placeholder="Seu nome"
                                    />
                                </div>
                                <div>
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        WhatsApp *
                                    </label>
                                    <input
                                        data-testid="quote-form-field-whatsapp"
                                        required
                                        value={form.whatsapp}
                                        onChange={set("whatsapp")}
                                        className={FIELD}
                                        placeholder="(00) 00000-0000"
                                    />
                                </div>
                                <div>
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Produto desejado *
                                    </label>
                                    <input
                                        data-testid="quote-form-field-produto"
                                        required
                                        value={form.produto}
                                        onChange={set("produto")}
                                        className={FIELD}
                                        placeholder="Ex.: canecas, cesta, camisetas..."
                                    />
                                </div>
                                <div>
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Quantidade
                                    </label>
                                    <input
                                        data-testid="quote-form-field-quantidade"
                                        value={form.quantidade}
                                        onChange={set("quantidade")}
                                        className={FIELD}
                                        placeholder="Ex.: 10 peças"
                                    />
                                </div>
                                <div>
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Data em que precisa do pedido
                                    </label>
                                    <input
                                        data-testid="quote-form-field-data"
                                        type="date"
                                        value={form.data}
                                        onChange={set("data")}
                                        className={FIELD}
                                    />
                                </div>
                                <div>
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Foto ou referência
                                    </label>
                                    <input
                                        data-testid="quote-form-field-referencia"
                                        type="file"
                                        accept="image/*"
                                        onChange={set("refFile")}
                                        className="w-full bg-white border border-ink/15 rounded-[3px] px-4 py-2.5 font-body text-xs text-ink file:mr-3 file:border-0 file:bg-obsidian file:text-cream file:px-3 file:py-1.5 file:rounded-full file:text-[10px] file:uppercase file:tracking-wider file:font-semibold"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Descrição da ideia *
                                    </label>
                                    <textarea
                                        data-testid="quote-form-field-ideia"
                                        required
                                        rows={4}
                                        value={form.ideia}
                                        onChange={set("ideia")}
                                        className={`${FIELD} resize-none`}
                                        placeholder="Conte sua ideia: nomes, frases, cores, para quem é..."
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="font-body text-xs font-semibold text-ink/70 block mb-2">
                                        Observações
                                    </label>
                                    <textarea
                                        data-testid="quote-form-field-observacoes"
                                        rows={2}
                                        value={form.obs}
                                        onChange={set("obs")}
                                        className={`${FIELD} resize-none`}
                                        placeholder="Prazo, local de entrega, dúvidas..."
                                    />
                                </div>
                            </div>

                            <p className="font-body text-xs text-inksoft mt-6">
                                Ao enviar, abriremos o WhatsApp com sua
                                mensagem pronta para concluir o pedido.
                            </p>
                            <button
                                data-testid="quote-form-submit"
                                type="submit"
                                className="mt-5 w-full sm:w-auto bg-obsidian text-cream font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.16em] px-10 py-4 rounded-full hover:bg-gold-deep transition-all duration-300 hover:-translate-y-0.5"
                            >
                                Solicitar orçamento
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
